import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import PropTypes from 'prop-types';

const BarcodeScannerComponent = (props) => {
    const { showScanner, setShowScanner, handleScanSuccess } = props;
    const scannerRef = useRef(null);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const qrboxWidth = Math.min(window.innerWidth * 0.8, 600);
    const qrboxHeight = qrboxWidth * 0.6;

    useEffect(() => {
        const updateHeight = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', updateHeight);

        if (showScanner && !scannerRef.current) {
            scannerRef.current = new Html5Qrcode('reader');
            startScanner();
        }

        return () => {
            if (scannerRef.current) {
                scannerRef.current.clear();
                scannerRef.current = null;
            }
            window.removeEventListener('resize', updateHeight);
        };
    }, [showScanner]);

    const startScanner = async () => {
        try {
            if (scannerRef.current) {
                await scannerRef.current.start(
                    { facingMode: 'environment' },
                    {
                        fps: 15,
                        formatsToSupport: [
                            'code_128',
                        ],
                        focusMode: "continuous",
                        qrbox: {
                            width: Math.min(window.innerWidth * 0.8, 300), // Adjust to a more universal size
                            height: Math.min(window.innerWidth * 0.6, 200), // Adjust height accordingly
                        },
                    },
                    onScanSuccess,
                    onScanFailure
                );
            }
        } catch (err) {
            console.error('Error starting scanner:', err);
        }
    };

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
            } catch (err) {
                console.error('Error stopping scanner:', err);
            } finally {
                scannerRef.current = null;
                setShowScanner(false);
            }
        }
    };

    const onScanSuccess = async (decodedText) => {
        await stopScanner();
        handleScanSuccess(decodedText);
    };

    const onScanFailure = (error) => {
        console.warn('QR code scan failed:', error);
    };

    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: `${viewportHeight}px`,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const readerContainerStyle = {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
    };

    const readerStyle = {
        width: '100vw',
        height: '100vh',
        display: 'block',
    };

    const qrboxStyle = {
        position: 'absolute',
        width: `${qrboxWidth}px`,
        height: `${qrboxHeight}px`,
        border: '4px solid #00FF00',
        boxSizing: 'border-box',
        zIndex: 1001,
    };

    const buttonStyle = {
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 20px',
        borderRadius: '5px',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: '#f44336',
        color: '#fff',
        zIndex: 1002,
    };

    return (
        <div>
            {showScanner && (
                <div style={overlayStyle}>
                    <div style={readerContainerStyle}>
                        <div id="reader" style={readerStyle}></div>
                        <div style={qrboxStyle}></div>
                    </div>
                    <button style={buttonStyle} onClick={stopScanner}>
                        Close Scanner
                    </button>
                </div>
            )}
        </div>
    );
};

BarcodeScannerComponent.propTypes = {
    showScanner: PropTypes.bool.isRequired,
    setShowScanner: PropTypes.func.isRequired,
    handleScanSuccess: PropTypes.func.isRequired
}

export default BarcodeScannerComponent;
